import { statusDurationTicks, statusIntervalTicks, type Vec2, type Enemy } from './state.js';
import { DEFAULT_AREA_INTERVAL_S } from '../types.js';
import { lineOfEffect, type Terrain } from '../terrain.js';
import { RootContextPool, type RootContextHandle } from './root-context.js';
import { resolveEnemyHitboxRadiusM } from './archetypes.js';

/** A persistent area ends at its placement point, independently of whether it ever pulses. */
export function persistentAreaLifecycle(durationS: number, tickRate: number) {
  return { durationTicks: statusDurationTicks(durationS, tickRate), endPoint: 'PLACEMENT' as const };
}

/** Contact ticks of a target that remains in an area; the same gate admits runtime contacts below. */
export function persistentAreaApplicationTicks(durationS: number, pulseIntervalS: number | undefined, collisionCheckIntervalS: number | undefined, tickRate: number, maxApplications: number): readonly number[] {
  const durationTicks = persistentAreaLifecycle(durationS, tickRate).durationTicks;
  const hitIntervalTicks = Math.max(1, statusIntervalTicks(pulseIntervalS ?? DEFAULT_AREA_INTERVAL_S, tickRate));
  const checkIntervalTicks = pulseIntervalS === undefined
    ? Math.max(1, statusIntervalTicks(collisionCheckIntervalS ?? DEFAULT_AREA_INTERVAL_S, tickRate)) : hitIntervalTicks;
  const hits: number[] = [];
  let last: number | undefined;
  for (let tick = checkIntervalTicks; tick <= durationTicks && hits.length < maxApplications; tick += checkIntervalTicks) {
    if (areaHitReady(tick, last, hitIntervalTicks)) { hits.push(tick); last = tick; }
  }
  return hits;
}

function areaHitReady(tick: number, last: number | undefined, hitIntervalTicks: number): boolean {
  return last === undefined || tick - last >= Math.max(1, hitIntervalTicks);
}

export type AreaKind = 'PULSE_AREA' | 'CONTINUOUS_AREA';
export type TargetReferencePolicy = 'SNAPSHOT_POINT' | 'KEEP_TARGET_HANDLE' | 'REQUERY_AT_EXECUTION';
export type ScheduledKind = 'DELAY' | 'REPEAT' | 'PULSE';
export interface ScheduledEvent { id: number; kind: ScheduledKind; atTick: number; intervalTicks?: number; run: () => boolean | void; cancel?: () => void; }
export type DelayTargetContext =
  | { policy: 'SNAPSHOT_POINT'; point: Vec2 }
  | { policy: 'KEEP_TARGET_HANDLE'; targetId: number }
  | { policy: 'REQUERY_AT_EXECUTION'; selector: () => readonly Enemy[] };
export interface DelayContinuation {
  root: RootContextHandle;
  nodeId: string;
  target: DelayTargetContext;
  run: (target: DelayTargetContext, root: RootContextHandle) => void;
}

/** Integer-tick buckets prevent floating point drift and repeat catch-up storms. */
export class TickBucketScheduler {
  private buckets = new Map<number, ScheduledEvent[]>(); private nextId = 1;
  private lastTick = -1;
  constructor(private readonly roots?: RootContextPool) {}
  schedule(kind: ScheduledKind, atTick: number, run: () => boolean | void, intervalTicks?: number, cancel?: () => void): number {
    const event = { id: this.nextId++, kind, atTick, intervalTicks, run, cancel };
    const bucket = this.buckets.get(atTick) ?? []; bucket.push(event); this.buckets.set(atTick, bucket); return event.id;
  }
  delay(currentTick: number, delayTicks: number, run: () => void): number { return this.schedule('DELAY', currentTick + Math.max(0, delayTicks), run); }
  delayContinuation(currentTick: number, delayTicks: number, continuation: DelayContinuation): number {
    const retained = this.roots?.retain(continuation.root) ?? false;
    let finalized = false;
    const finalize = () => {
      if (finalized) return;
      finalized = true;
      if (retained) {
        this.roots!.release(continuation.root);
        this.roots!.cleanupTickEnd();
      }
    };
    return this.schedule('DELAY', currentTick + Math.max(0, delayTicks), () => {
      try {
        if (!this.roots || this.roots.get(continuation.root)) continuation.run(continuation.target, continuation.root);
      } finally {
        finalize();
      }
    }, undefined, finalize);
  }
  cancel(id: number): boolean {
    for (const [tick, bucket] of this.buckets) {
      const index = bucket.findIndex((event) => event.id === id);
      if (index < 0) continue;
      const [event] = bucket.splice(index, 1);
      if (bucket.length === 0) this.buckets.delete(tick);
      event?.cancel?.();
      return true;
    }
    return false;
  }
  clear(): void {
    for (const bucket of this.buckets.values()) for (const event of bucket) event.cancel?.();
    this.buckets.clear();
  }
  tick(currentTick: number): void {
    if (currentTick <= this.lastTick) return;
    this.lastTick = currentTick;
    const due = this.buckets.get(currentTick) ?? []; this.buckets.delete(currentTick);
    // An occurrence exactly on the inclusive lifetime boundary is gameplay;
    // lifecycle DELAY cleanup for that tick runs after PULSE/REPEAT work.
    due.sort((a, b) => Number(a.kind === 'DELAY') - Number(b.kind === 'DELAY') || a.id - b.id);
    for (let index = 0; index < due.length; index++) {
      const event = due[index]!;
      try {
        const keep = event.run();
        if (keep !== false && (event.kind === 'REPEAT' || event.kind === 'PULSE') && (event.intervalTicks ?? 0) > 0) {
          this.schedule(event.kind, currentTick + event.intervalTicks!, event.run, event.intervalTicks, event.cancel);
        }
      } catch (error) {
        // The due bucket has already been detached. Finalize every occurrence
        // that will no longer be visited before propagating the original error.
        // Cleanup failures must not mask the callback failure or prevent later
        // events in the detached bucket from releasing their owned resources.
        try { event.cancel?.(); } catch { /* preserve the original run error */ }
        for (let pending = index + 1; pending < due.length; pending++) {
          try { due[pending]!.cancel?.(); } catch { /* continue finalizing the bucket */ }
        }
        throw error;
      }
    }
  }
}

export interface AreaSpec { kind: AreaKind; radiusM: number; collisionCheckIntervalTicks: number; hitIntervalTicks: number; targetPolicy: TargetReferencePolicy; }
export interface AreaInstance {
  spec: Readonly<AreaSpec>; point: Vec2; targetId?: number; nextCollisionTick: number; lastHitTick: Map<number, number>;
  /** The point the latest `advanceArea` contact check was taken around (absent when the policy resolved no point). */
  checkedPoint?: Vec2;
}
export function advanceArea(
  area: AreaInstance,
  tick: number,
  enemies: readonly Enemy[],
  resolveTarget: (id: number) => Vec2 | null,
  requeryPoint: () => Vec2 | null = () => null,
  terrain?: Terrain,
): Enemy[] {
  if (area.spec.kind === 'CONTINUOUS_AREA' && tick < area.nextCollisionTick) return [];
  area.nextCollisionTick = area.spec.kind === 'CONTINUOUS_AREA' ? tick + Math.max(1, area.spec.collisionCheckIntervalTicks) : tick;
  const point = area.spec.targetPolicy === 'KEEP_TARGET_HANDLE'
    ? area.targetId === undefined ? null : resolveTarget(area.targetId)
    : area.spec.targetPolicy === 'REQUERY_AT_EXECUTION' ? requeryPoint() : area.point;
  area.checkedPoint = point ? { ...point } : undefined;
  if (!point) return [];
  const candidates = enemies.filter((enemy) => {
    if (!enemy.alive || Math.hypot(enemy.pos.x - point.x, enemy.pos.y - point.y) > area.spec.radiusM + resolveEnemyHitboxRadiusM(enemy.archetype)) return false;
    if (terrain && !lineOfEffect(terrain, point, enemy.pos, 'areaEffect')) return false;
    return true;
  }).sort((a, b) => Math.hypot(a.pos.x - point.x, a.pos.y - point.y) - Math.hypot(b.pos.x - point.x, b.pos.y - point.y) || a.id - b.id);
  const capped = candidates;
  return capped.filter((enemy) => {
    const last = area.lastHitTick.get(enemy.id);
    if (!areaHitReady(tick, last, area.spec.hitIntervalTicks)) return false;
    area.lastHitTick.set(enemy.id, tick); return true;
  });
}
